import i18n from '@/i18n';
import '@/styles/desktop-app.scss';
import '@/styles/document-root.css';
import '@dabble/quillui/global';
import '@dabble/quillui/styles';
import '@fontsource-variable/dm-sans';
import '@fontsource-variable/inter';
import '@fontsource/pt-serif';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import posthog from 'posthog-js';
import { createApp } from 'vue';
import {
  POSTHOG_ENVIRONMENT,
  POSTHOG_HOST,
  POSTHOG_TOKEN,
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  REWARDFUL_ID,
  SENTRY_RELEASE,
  TOLT_ID,
} from './lib/env';
import App from './App.vue';
import { initMarketing, trackMarketingPageView } from './lib/marketing';
import { installPreloadErrorReload } from './lib/preloadErrorReload';
import { loadRewardful } from './lib/rewardful';
import { sentryBeforeSend, sentryDenyUrls, sentryIgnoreErrors } from './lib/sentryFilters';
import { installStorageFallback } from './lib/storageFallback';
import { loadTolt } from './lib/tolt';
import router from './router';
import { useAccountsStore } from './stores/accounts';
import { initDesktop } from './use/desktop';

// An in-app WebView with DOM storage off exposes localStorage/sessionStorage as
// null; stand in an in-memory store before anything reads one, or boot throws
// on the first read (DABBLE-ACCOUNTS-2-50).
installStorageFallback();

// Recover from stale-deploy asset failures: a deploy replaces the content-hashed
// chunks under /assets/, so a tab loaded before the deploy 404s on any chunk it
// lazily imports afterwards. Vite dispatches `vite:preloadError` for those
// failures; reload once (loop-guarded) to pick up the new build. Installed first
// so the listener is registered before any dynamic import can fire.
installPreloadErrorReload();

const app = createApp(App);

// Skip Sentry on local-dev so developer-machine errors don't reach Sentry; test/prod still captured.
if (typeof window !== 'undefined' && SENTRY_DSN && SENTRY_ENVIRONMENT !== 'local') {
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    // 1.0 traced every page load and API call: ~1.9M spans a month against
    // the 5M the plan includes, for an app whose traces nobody has needed to
    // read at full fidelity. A fifth keeps the performance picture (route
    // timings are aggregates) with headroom to spare.
    tracesSampleRate: 0.2,
    // Replays are billed per recording past a small allowance, and the ones
    // that get watched are the ones attached to an error - kept at 1.0 below.
    // 0.1 recorded ~1,700 healthy sessions a month; 1 in 100 keeps a
    // baseline for "what does a normal signup look like" without paying for
    // the other 99.
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    // Browser-extension / in-app-browser chatter with no app frames; see
    // lib/sentryFilters.ts for each pattern and why fetch failures stay out.
    ignoreErrors: sentryIgnoreErrors,
    // Frames inside a browser extension's own code.
    denyUrls: sentryDenyUrls,
    // Injected-script noise that needs the event's frames, not just its message.
    beforeSend: sentryBeforeSend,
  });
}

// PostHog is gated on environment the same way Sentry is above. It matters more
// here than in the writer app: the project token has a baked default
// (`lib/env.ts`), so a local build reached the production project with no env
// file at all — every `npm run dev` was billing it.
//
// Local initializes the SDK INERT rather than skipping init. `capture()`,
// `identify()` and `reset()` each log an uninitialized warning when init never
// ran, and this app calls them from the auth store, the accounts store and
// `App.vue` — a skipped init would put warnings through every local sign-in.
const posthogReports = typeof window !== 'undefined' && POSTHOG_ENVIRONMENT !== 'local';

posthog.init(POSTHOG_TOKEN, {
  api_host: POSTHOG_HOST,
  defaults: '2026-01-30',
  // The local gate: nothing sent, no flag request out, nothing written to
  // storage. The persistence half is not redundant — opting out of capturing
  // does not opt out of persistence, and PostHog's cookie is written on
  // `.dabblewriter.com`, the same one production reads. Without it a dev
  // session leaves `environment: 'local'` and its `distinct_id` behind for the
  // next production visit in that browser to adopt.
  opt_out_capturing_by_default: !posthogReports,
  opt_out_persistence_by_default: !posthogReports,
  advanced_disable_flags: !posthogReports,
  // This app was running PostHog on its defaults, which meant `autocapture`
  // on: a billed `$autocapture` event for every click, change and submit on
  // the signup, login and billing surface — the highest-traffic and most
  // bot-scraped pages we own, and the ones where a crawler generates events
  // indistinguishable from a person's. Nothing reads the autocapture stream,
  // and it was never a decision here: `posthog.init` arrived with 09eed93
  // ("working accounts") and no commit has touched an analytics option since.
  // The writer app set this `false` in its first PostHog commit seven days
  // earlier; the two diverged by omission.
  autocapture: false,
  // Web Vitals arrive with the defaults preset. Nothing reads them here (no
  // insight, no dashboard), and they were the second-largest event on this
  // project after the autocapture fix - ~4,500 in five days against ~5,400
  // pageviews. Performance of signup and billing is watched in Sentry's
  // tracing (sampled above), which carries the route and the API timings.
  capture_performance: { web_vitals: false },
  // `defaults: '2026-01-30'` sets `capture_pageview: 'history_change'` and
  // leaves `capture_pageleave` at `'if_capture_pageview'`, so each step of a
  // multi-screen signup billed two events.
  //
  // `true`, not `false`: the SDK fires its initial pageview for `true` OR
  // `'history_change'`, while only `'history_change'` arms the per-navigation
  // listener — so this is one pageview per load and none per step, and it keeps
  // what a hand-rolled `capture('$pageview')` would lose (deferral until the tab
  // is actually visible, the `title` property, and firing after `register()`).
  //
  // Note what this app does NOT have: a funnel in PostHog. `signup_affiliate_
  // attribution` is its only named event — signup and checkout conversion is
  // tracked through the GA4 / Google Ads / Facebook / Pinterest pixels in
  // `initMarketing` below. Per-navigation pageviews were not standing in for
  // that instrumentation; nothing was reading them either.
  capture_pageview: true,
  // Pageviews are wanted, pageleaves are not — set explicitly so the
  // `'if_capture_pageview'` default can't follow `capture_pageview` back on.
  // The cost is that Web Analytics has no session-duration or bounce signal
  // for this app.
  capture_pageleave: false,
  // Sentry already records this app's sessions (`replayIntegration`, 10%
  // sampled, above). PostHog was recording them too — the same sessions,
  // billed twice, on two vendors — because `disable_session_recording`
  // defaults to false and the project has replay switched on. Sentry keeps
  // the job here: its replay is already wired to this app's errors via
  // `replaysOnErrorSampleRate`, which is when anyone actually watches one.
  disable_session_recording: true,
});
posthog.register({ environment: POSTHOG_ENVIRONMENT });

// Ad / conversion tracking pixels (GA4, Google Ads, Facebook, Pinterest).
// Production host only, and only when ids are configured — forks, PR previews,
// local, and test builds stay inert. Registered before the router so the first
// navigation is counted; SPA navigations forward on every `afterEach`.
initMarketing();
// Affiliate click tracking. Kept out of `initMarketing` because these carry their
// own gate (the ids are set on production, or in a build that opts in) and
// because signup and checkout depend on them: the click ids they publish are what
// dabble-rest hands to Stripe. Loaded before the router so they read the
// affiliate's `?via=` off the landing URL, ahead of any redirect that rewrites it.
// Rewardful runs alongside Tolt until the affiliates have moved over.
loadTolt(TOLT_ID);
loadRewardful(REWARDFUL_ID);
router.afterEach((to) => {
  trackMarketingPageView({ path: to.fullPath, title: document.title });
});

// Sentry.init({ app }) attaches its own Vue errorHandler; setting one here
// replaces it, so forward explicitly. Sentry only — capturing into PostHog as
// well billed a second copy of an error we only ever read in Sentry, which is
// where this app's releases, source maps and `ignoreErrors` live.
app.config.errorHandler = (err) => {
  if (err instanceof Error) {
    Sentry.captureException(err);
  }
};

// Listen for the desktop shell's fullscreen changes, which decide whether the window still
// needs the title bar App.vue draws for it.
initDesktop();

const pinia = createPinia();
app.use(pinia);
app.use(i18n);

// Kick off auth resolution without blocking app mount. The router guard
// awaits the same idempotent promise via accountsStore.init(), and
// App.vue shows a <Loader/> until userDataReady flips true.
useAccountsStore().init();

app.use(router);

app.mount('#app');
